import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Badge, Box, Divider, Group, Pagination, Select, Skeleton, Stack, Text } from '@mantine/core';

import { useListTasksQuery } from '@/store/slices/api/tasksApiSlice';
import { TaskBusinessState } from '@/types/task';
import { STATUS_META } from '@/utils/taskConstants';
import routes from '@/routes/config';

const STATUS_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'completed', label: 'Completed' },
  { value: 'canceled', label: 'Canceled' },
];

function TaskRow({ task, href, onNavigate, showDivider }: {
  task: import('@/types/task').TaskResponseDto;
  href: string;
  onNavigate: () => void;
  showDivider: boolean;
}) {
  const [hovered, setHovered] = useState(false);
  return (
    <Box>
      <Box
        component="a"
        href={href}
        onClick={(e) => { e.preventDefault(); onNavigate(); }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        py={14}
        px={8}
        style={{
          display: 'block',
          textDecoration: 'none',
          borderRadius: 6,
          background: hovered ? 'var(--mantine-color-gray-0)' : 'transparent',
          transition: 'background 0.12s',
          cursor: 'pointer',
        }}>
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Box style={{ minWidth: 0 }}>
            <Group gap={8} wrap="nowrap" mb={4}>
              <Text
                fw={700}
                size="sm"
                truncate
                style={{
                  color: hovered ? 'var(--mantine-color-plum-7)' : 'var(--mantine-color-dark-9)',
                  textDecoration: hovered ? 'underline' : 'none',
                  transition: 'color 0.12s',
                }}>
                {task.subject} — {task.organization?.name ?? '—'}
              </Text>
              <Badge
                size="sm"
                radius="sm"
                variant="light"
                color={STATUS_META[task.businessState].color}
                style={{ whiteSpace: 'nowrap', flexShrink: 0 }}>
                {STATUS_META[task.businessState].label}
              </Badge>
            </Group>
            <Text size="xs" c="dimmed">
              {task.taskNumber} · {task.taskType?.name ?? '—'} · {formatDate(task.createdAt)}
            </Text>
          </Box>
        </Group>
      </Box>
      {showDivider && <Divider />}
    </Box>
  );
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

interface PatientTasksListProps {
  patientId: string;
}

export default function PatientTasksList({ patientId }: PatientTasksListProps) {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState('');
  const [taskTypeFilter, setTaskTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    setPage(1);
  }, [statusFilter, taskTypeFilter]);

  const { data: tasksData, isLoading, isFetching } = useListTasksQuery({
    patientId,
    ...(statusFilter ? { businessStates: [statusFilter as TaskBusinessState] } : {}),
    ...(taskTypeFilter ? { taskTypeIds: [taskTypeFilter] } : {}),
    limit: pageSize,
    skip: (page - 1) * pageSize,
  });

  const { data: typeSource } = useListTasksQuery({
    patientId,
    ...(statusFilter ? { businessStates: [statusFilter as TaskBusinessState] } : {}),
    limit: 100,
    skip: 0,
  });

  const tasks = tasksData?.items ?? [];

  const typeOptions = useMemo(() => {
    const names = new Map<string, string>();
    for (const task of typeSource?.items ?? []) {
      if (task.taskType?.id && task.taskType.name) names.set(task.taskType.id, task.taskType.name);
    }
    return [
      { value: '', label: 'All Types' },
      ...[...names.entries()]
        .sort((a, b) => a[1].localeCompare(b[1]))
        .map(([id, name]) => ({ value: id, label: name })),
    ];
  }, [typeSource]);

  useEffect(() => {
    if (taskTypeFilter && !typeOptions.some((option) => option.value === taskTypeFilter)) {
      setTaskTypeFilter('');
    }
  }, [taskTypeFilter, typeOptions]);

  return (
    <Stack gap={0}>
      {/* Filters */}
      <Group gap={12} mb={20}>
        <Box style={{ flex: '0 0 180px' }}>
          <Text size="xs" fw={600} c="dimmed" mb={4}>Status</Text>
          <Select
            data={STATUS_OPTIONS}
            value={statusFilter}
            onChange={(v) => setStatusFilter(v ?? '')}
            size="sm"
          />
        </Box>
        <Box style={{ flex: '0 0 240px' }}>
          <Text size="xs" fw={600} c="dimmed" mb={4}>Type</Text>
          <Select
            data={typeOptions}
            value={taskTypeFilter}
            onChange={(v) => setTaskTypeFilter(v ?? '')}
            size="sm"
          />
        </Box>
      </Group>

      {/* List */}
      {isLoading || isFetching ? (
        <Stack gap={0}>
          {[...Array(6)].map((_, i) => (
            <Box key={i} py={14} px={8}>
              <Group gap={8} mb={6} wrap="nowrap">
                <Skeleton height={14} width="45%" radius="sm" />
                <Skeleton height={20} width={72} radius="sm" />
              </Group>
              <Skeleton height={11} width="30%" radius="sm" />
            </Box>
          ))}
        </Stack>
      ) : tasks.length === 0 ? (
        <Text c="dimmed" size="sm" mt={24} ta="center">
          No tasks found for this patient.
        </Text>
      ) : (
        <Stack gap={0}>
          {tasks.map((task, i) => (
            <TaskRow
              key={task.id}
              task={task}
              href={routes.physician.taskDetail(task.id)}
              onNavigate={() => navigate(routes.physician.taskDetail(task.id))}
              showDivider={i < tasks.length - 1}
            />
          ))}
          {(page > 1 || tasks.length === pageSize) && (
            <Group justify="center" mt="md">
              <Pagination
                total={tasks.length === pageSize ? page + 1 : page}
                value={page}
                onChange={setPage}
                size="sm"
                color="plum"
              />
            </Group>
          )}
        </Stack>
      )}
    </Stack>
  );
}
